import { useCallback, useMemo, useRef } from 'react';

import {
  appendSearchCriteriaParams,
  readSearchCriteriaUrlStateFromAsPath,
} from '../searchCriteria/urlState';
import { useSearchCriteria } from '../searchCriteria/useSearchCriteria';
import type { MultiListSearchDefinitions, MultiListUrlStateMap } from '../searchListState/types';
import {
  appendSearchListParams,
  readSearchListUrlStateFromAsPath,
  resolveSearchListParamNames,
} from '../searchListState/urlState';
import { useMultiSearchListState } from '../searchListState/useMultiSearchListState';
import { serializeCriteria, serializeListUrlState, serializeRecord } from '../serialization';
import { defaultQueryParamName } from '../sharedConstants';
import type { SearchPageCriteria } from '../sharedTypes';
import { useSearchPageUrlSync } from '../urlState';
import { useSearchFacetsState, type SearchFacetsFetchResult } from '../useSearchFacetsState';
import { useSearchPageCriteriaController } from '../useSearchPageCriteriaController';
import type { UseMultiListSearchPageOptions, UseMultiListSearchPageResult } from './types';

type MultiListUrlState<
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
> = {
  criteria: SearchPageCriteria<FilterName>;
  lists: MultiListUrlStateMap<FilterName, Lists>;
};

const mapListsForUrlSerialization = <
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
>(
  listState: MultiListUrlStateMap<FilterName, Lists>
) =>
  Object.keys(listState).reduce<Record<string, string>>((serializedLists, listKey) => {
    serializedLists[listKey] = serializeListUrlState({
      take: listState[listKey as keyof typeof listState].take,
      sort: listState[listKey as keyof typeof listState].sort,
    });
    return serializedLists;
  }, {});

export const useMultiListSearchPage = <
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
>(
  options: UseMultiListSearchPageOptions<FilterName, Lists>
): UseMultiListSearchPageResult<FilterName, Lists> => {
  const queryParamName = options.queryParamName ?? defaultQueryParamName;
  const criteriaState = useSearchCriteria({
    filters: options.filters,
  });
  const listKeys = useMemo(
    () => Object.keys(options.lists) as Array<keyof Lists & string>,
    [options.lists]
  );
  const listParamNames = useMemo(
    () =>
      listKeys.reduce<Record<string, ReturnType<typeof resolveSearchListParamNames>>>(
        (paramNames, listKey) => {
          paramNames[listKey] = resolveSearchListParamNames({
            listKey,
            namespaced: true,
            takeParamName: options.lists[listKey].takeParamName,
            sortParamName: options.lists[listKey].sortParamName,
          });
          return paramNames;
        },
        {}
      ),
    [listKeys, options.lists]
  );

  const criteriaRef = useRef<SearchPageCriteria<FilterName>>(criteriaState.appliedCriteria);
  const currentUrlStateRef = useRef<MultiListUrlState<FilterName, Lists>>({
    criteria: criteriaState.appliedCriteria,
    lists: listKeys.reduce<MultiListUrlStateMap<FilterName, Lists>>(
      (state, listKey) => {
        state[listKey] = {
          take: options.lists[listKey].pageSize,
          sort: options.lists[listKey].defaultSort ?? null,
        } as MultiListUrlStateMap<FilterName, Lists>[typeof listKey];
        return state;
      },
      {} as MultiListUrlStateMap<FilterName, Lists>
    ),
  });
  const applyUrlStateRef = useRef<(urlState: MultiListUrlState<FilterName, Lists>) => void>(
    () => {}
  );

  const readUrlStateFromAsPath = useCallback(
    (asPath: string) => {
      const nextCriteriaUrlState = readSearchCriteriaUrlStateFromAsPath({
        asPath,
        filterDefinitions: options.filters,
        filterNames: criteriaState.filterNames,
        queryParamName,
      });

      const nextListsState = listKeys.reduce<MultiListUrlStateMap<FilterName, Lists>>(
        (state, listKey) => {
          const definition = options.lists[listKey];
          const { takeParamName, sortParamName } = listParamNames[listKey];

          state[listKey] = readSearchListUrlStateFromAsPath({
            asPath,
            pageSize: definition.pageSize,
            takeParamName,
            sortParamName,
            sortOptions: definition.sortOptions,
            defaultSort: definition.defaultSort,
          }) as MultiListUrlStateMap<FilterName, Lists>[typeof listKey];

          return state;
        },
        {} as MultiListUrlStateMap<FilterName, Lists>
      );

      return {
        criteria: nextCriteriaUrlState,
        lists: nextListsState,
      };
    },
    [
      criteriaState.filterNames,
      listKeys,
      listParamNames,
      options.filters,
      options.lists,
      queryParamName,
    ]
  );

  const serializeUrlState = useCallback(
    (urlState: MultiListUrlState<FilterName, Lists>) =>
      JSON.stringify({
        criteria: JSON.parse(serializeCriteria(urlState.criteria, criteriaState.filterNames)),
        lists: JSON.parse(serializeRecord(mapListsForUrlSerialization(urlState.lists))),
      }),
    [criteriaState.filterNames]
  );

  const buildSearchParams = useCallback(
    (urlState: MultiListUrlState<FilterName, Lists>) => {
      const params = new URLSearchParams();

      appendSearchCriteriaParams({
        params,
        urlState: urlState.criteria,
        filterDefinitions: options.filters,
        filterNames: criteriaState.filterNames,
        queryParamName,
      });

      listKeys.forEach((listKey) => {
        const definition = options.lists[listKey];
        const { takeParamName, sortParamName } = listParamNames[listKey];

        appendSearchListParams({
          params,
          urlState: {
            take: urlState.lists[listKey].take,
            sort: urlState.lists[listKey].sort,
          },
          pageSize: definition.pageSize,
          takeParamName,
          sortParamName,
          defaultSort: definition.defaultSort,
        });
      });

      return params;
    },
    [
      criteriaState.filterNames,
      listKeys,
      listParamNames,
      options.filters,
      options.lists,
      queryParamName,
    ]
  );

  const { hasHydratedFromUrl, setNavigationMode } = useSearchPageUrlSync({
    readUrlStateFromAsPath,
    applyUrlState: (urlState) => applyUrlStateRef.current(urlState),
    getCurrentUrlState: () => currentUrlStateRef.current,
    serializeUrlState,
    buildSearchParams,
  });

  const listExecution = useMultiSearchListState({
    enabled: hasHydratedFromUrl,
    criteria: criteriaState.appliedCriteria,
    lists: options.lists,
    listKeys,
  });

  const serializedCriteria = serializeCriteria(
    criteriaState.appliedCriteria,
    criteriaState.filterNames
  );
  const facetsFetcher = options.facetsFetcher;
  const facetsState = useSearchFacetsState<FilterName>({
    // Matches the list-execution convention; pre-hydration criteria are empty and would
    // resolve to the seed anyway. Without a fetcher the facets state is inert (never fetches).
    enabled: hasHydratedFromUrl && facetsFetcher !== undefined,
    serializedCriteria,
    criteria: criteriaState.appliedCriteria,
    fetcher: facetsFetcher ?? (async (): Promise<SearchFacetsFetchResult<FilterName>> => ({})),
    initialFacetState: options.initialFacetState,
  });

  criteriaRef.current = criteriaState.appliedCriteria;
  currentUrlStateRef.current = {
    criteria: criteriaRef.current,
    lists: listKeys.reduce<MultiListUrlStateMap<FilterName, Lists>>(
      (state, listKey) => {
        state[listKey] = {
          take: listExecution.listsState[listKey].take,
          sort: listExecution.listsState[listKey].sort,
        } as MultiListUrlStateMap<FilterName, Lists>[typeof listKey];
        return state;
      },
      {} as MultiListUrlStateMap<FilterName, Lists>
    ),
  };
  applyUrlStateRef.current = (urlState) => {
    criteriaState.applyUrlState(urlState.criteria);
    listExecution.applyUrlState(urlState.criteria, urlState.lists);
  };

  const criteriaController = useSearchPageCriteriaController({
    criteriaState,
    filterDefinitions: options.filters,
    getCurrentCriteriaState: () => ({
      queryDraft: criteriaState.queryDraft,
      query: criteriaRef.current.query,
      selectedFilters: criteriaRef.current.filters,
    }),
    setNavigationMode,
    onCriteriaChange: (nextCriteria) => listExecution.reloadAfterCriteriaChange(nextCriteria),
    onClearAll: (nextCriteria) => listExecution.resetAllAndReload(nextCriteria),
  });

  const loadMore = (listKey: keyof Lists & string) => {
    setNavigationMode('replace');
    listExecution.loadMore(listKey);
  };

  const setListSort = (listKey: keyof Lists & string, nextSort: string | null) => {
    setNavigationMode('replace');
    listExecution.setListSort(listKey, nextSort);
  };

  const lists = useMemo(
    () =>
      listKeys.reduce<UseMultiListSearchPageResult<FilterName, Lists>['lists']>(
        (result, listKey) => {
          const definition = options.lists[listKey];
          const listState = listExecution.listsState[listKey];
          const nextListResult: UseMultiListSearchPageResult<
            FilterName,
            Lists
          >['lists'][typeof listKey] = {
            items: listState.items,
            loading: listState.loading,
            hasLoaded: listState.hasLoaded,
            error: listState.error,
            hasMore: listState.hasMore,
            totalCount: listState.totalCount,
            take: listState.take,
            loadMore: () => loadMore(listKey),
          };

          if (definition.sortOptions && definition.sortOptions.length > 0) {
            nextListResult.sort = listState.sort as typeof nextListResult.sort;
            nextListResult.setSort = (nextSort) => setListSort(listKey, nextSort);
            nextListResult.sortOptions =
              definition.sortOptions as typeof nextListResult.sortOptions;
          }

          result[listKey] = nextListResult;
          return result;
        },
        {} as UseMultiListSearchPageResult<FilterName, Lists>['lists']
      ),
    [listExecution.listsState, listKeys, options.lists]
  );

  return {
    hasHydratedFromUrl,
    query: criteriaState.query,
    queryDraft: criteriaState.queryDraft,
    setQueryDraft: criteriaState.setQueryDraft,
    submitQuery: criteriaController.submitQuery,
    clearQuery: criteriaController.clearQuery,
    filters: criteriaController.filters,
    facetState: facetsState.facetState,
    isFetching: facetsState.isFetching,
    activeFilters: criteriaController.activeFilters,
    hasActiveFilters: criteriaController.hasActiveFilters,
    clearFilter: criteriaController.clearFilter,
    clearAllFilters: criteriaController.clearAllFilters,
    clearAll: criteriaController.clearAll,
    lists,
  };
};
